import type { BlockType } from '../constants/blocks.ts';
import type { PaceCode, TopicCode, LearningFocusCode } from '../constants/languages.ts';
import { PACE_TIERS } from '../constants/languages.ts';
import { getChapterForBlock } from './chapters.ts';

// =============================================================================
// Block slot — a planned block before generation
// =============================================================================

export interface BlockSlot {
  /** Block type to generate. */
  type: BlockType;
  /** Position in the workbook (1-based). */
  order: number;
  /** Sub-theme assigned to this block. */
  subTheme: string;
  /** Chapter number (1-based). Present only for complete workbooks. */
  chapter?: number;
}

// =============================================================================
// Composition input
// =============================================================================

export interface CompositionInput {
  pace: PaceCode;
  topics: TopicCode[];
  workbookTheme: string;
  /** Optional: sub-themes to distribute. If not provided, derived from topics. */
  subThemes?: string[];
  /** Sub-themes used in previous months' workbooks. These get deprioritized so
   *  the learner sees fresh topics first. */
  previousMonthsSubThemes?: string[];
  /** Optional: learning focus areas that adjust block type weighting. */
  learningFocus?: LearningFocusCode[];
}

// =============================================================================
// Block count table — derived from PACE_TIERS
// =============================================================================
// These match the constants in packages/shared/src/constants/languages.ts:
//
// | Pace      | Pages | Total Blocks | Conversations | Vocab Grids | Other |
// |-----------|-------|-------------|---------------|-------------|-------|
// | light     | 24    | 8-9         | 1             | 2           | 5-6   |
// | regular   | 32    | 11-12       | 2             | 3           | 6-7   |
// | intensive | 40    | 15-16       | 3             | 4           | 8-9   |
// =============================================================================

interface PaceComposition {
  conversations: number;
  vocabGrids: number;
  fillBlanks: number;
  readingComp: number;
  matching: number;
  scenarios: number;
  translations: number;
  conjugations: number;
  grammarExplanations: number;
}

/**
 * Fixed block counts per pace tier. These sum to the midpoint of the
 * min-max range from PACE_TIERS. Favorites (vocab, fill-blank, reading,
 * matching) get higher counts.
 */
const PACE_COMPOSITION: Record<PaceCode, PaceComposition> = {
  light: {
    conversations: 1,
    vocabGrids: 1,
    fillBlanks: 1,
    readingComp: 1,
    matching: 1,
    scenarios: 1,
    translations: 1,
    conjugations: 0,
    grammarExplanations: 1,
  }, // total: 8

  regular: {
    conversations: 2,
    vocabGrids: 2,
    fillBlanks: 1,
    readingComp: 1,
    matching: 1,
    scenarios: 1,
    translations: 1,
    conjugations: 1,
    grammarExplanations: 2,
  }, // total: 12

  intensive: {
    conversations: 3,
    vocabGrids: 3,
    fillBlanks: 1,
    readingComp: 2,
    matching: 1,
    scenarios: 1,
    translations: 1,
    conjugations: 1,
    grammarExplanations: 3,
  }, // total: 16

  complete: {
    conversations: 8,
    vocabGrids: 12,
    fillBlanks: 6,
    readingComp: 8,
    matching: 8,
    scenarios: 4,
    translations: 6,
    conjugations: 8,
    grammarExplanations: 0,
  }, // total: 60
};

// =============================================================================
// Focus-based composition adjustments
// =============================================================================

/**
 * Maps each learning focus code to block type adjustments.
 * Positive values = add blocks of that type, negative = remove.
 * The total adjustment should net to 0 (redistributes, doesn't change total).
 */
const FOCUS_ADJUSTMENTS: Record<
  LearningFocusCode,
  Partial<Record<keyof PaceComposition, number>>
> = {
  vocabulary: { vocabGrids: +1, scenarios: -1 },
  conversation: { conversations: +1, conjugations: -1 },
  grammar: { grammarExplanations: +1, conjugations: +1, matching: -1, scenarios: -1 },
  reading: { readingComp: +1, matching: -1 },
  translation: { translations: +1, matching: -1 },
};

/**
 * Adjusts the block composition based on the learner's focus areas.
 * For `light` pace: caps adjustments at +/-1 total per focus (too few blocks).
 * For `complete` pace: uses +/-2 per focus for more noticeable effect.
 * Ensures no block count goes below 0 and total stays the same.
 */
function applyFocusAdjustments(
  base: PaceComposition,
  focus: LearningFocusCode[],
  pace: PaceCode,
): PaceComposition {
  const result = { ...base };
  const scale = pace === 'complete' ? 2 : 1;

  for (const focusCode of focus) {
    const adjustments = FOCUS_ADJUSTMENTS[focusCode];
    if (!adjustments) continue;

    for (const [key, delta] of Object.entries(adjustments) as [keyof PaceComposition, number][]) {
      const scaledDelta = delta * scale;
      const newValue = result[key] + scaledDelta;

      // Don't go below 0
      if (newValue < 0) continue;

      result[key] = newValue;
    }
  }

  // Ensure total block count stays the same as original
  const originalTotal = Object.values(base).reduce((sum, v) => sum + v, 0);
  const newTotal = Object.values(result).reduce((sum, v) => sum + v, 0);
  const diff = newTotal - originalTotal;

  if (diff !== 0) {
    // Redistribute the difference by adjusting a neutral block type
    // Try scenarios first, then matching, then fillBlanks
    const adjustTargets: (keyof PaceComposition)[] = [
      'scenarios',
      'matching',
      'fillBlanks',
      'translations',
    ];
    for (const target of adjustTargets) {
      const adjusted = result[target] - diff;
      if (adjusted >= 0) {
        result[target] = adjusted;
        break;
      }
    }
  }

  return result;
}

// =============================================================================
// Sub-theme generation
// =============================================================================

/**
 * Expands the user's selected topics into concrete sub-themes.
 * Each topic yields 2-3 specific sub-themes that Claude will use.
 */
const TOPIC_SUB_THEMES: Record<string, string[]> = {
  travel: [
    'At the airport',
    'Hotel check-in',
    'Asking for directions',
    'Public transport',
    'Currency exchange',
    'Tourist information',
    'Travel insurance',
    'Car rental',
  ],
  work: [
    'Office conversations',
    'Business meetings',
    'Email communication',
    'Job interview',
    'Networking events',
    'Performance reviews',
    'Office supplies',
    'Business travel',
  ],
  medical: [
    'At the doctor',
    'At the pharmacy',
    'Describing symptoms',
    'Health insurance',
    'Emergency room',
    'Mental health',
    'Dental visit',
    'Allergies',
  ],
  social: [
    'Meeting new people',
    'Making plans',
    'At a party',
    'Dating & relationships',
    'Neighbors',
    'Online conversations',
    'Group activities',
    'Celebrations',
  ],
  food: [
    'Ordering at a restaurant',
    'At the market',
    'Cooking vocabulary',
    'Food allergies & preferences',
    'Street food',
    'Dietary needs',
    'Wine & beverages',
    'Recipes',
  ],
  shopping: [
    'Buying clothes',
    'At the electronics store',
    'Returning items',
    'Bargaining',
    'Online shopping',
    'Grocery comparison',
    'Second-hand',
    'Gift shopping',
  ],
  daily: [
    'Morning routine',
    'Household chores',
    'Grocery shopping',
    'Making appointments',
    'Banking',
    'Public services',
    'Pet care',
    'Home maintenance',
  ],
  culture: [
    'Museums & art',
    'Music & concerts',
    'Local festivals',
    'Cinema & theatre',
    'Sports events',
    'Literature',
    'Historical sites',
    'Local traditions',
  ],
};

function generateSubThemes(
  topics: TopicCode[],
  count: number,
  previousMonthsSubThemes?: string[],
): string[] {
  const allSubThemes: string[] = [];
  for (const topic of topics) {
    const subs = TOPIC_SUB_THEMES[topic] || [];
    allSubThemes.push(...subs);
  }

  // Sort unused sub-themes first, previously-used ones last
  if (previousMonthsSubThemes && previousMonthsSubThemes.length > 0) {
    const usedSet = new Set(previousMonthsSubThemes.map((s) => s.toLowerCase()));
    allSubThemes.sort((a, b) => {
      const aUsed = usedSet.has(a.toLowerCase()) ? 1 : 0;
      const bUsed = usedSet.has(b.toLowerCase()) ? 1 : 0;
      return aUsed - bUsed;
    });
  }

  // If not enough sub-themes, cycle through them
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    result.push(allSubThemes[i % allSubThemes.length] || 'General practice');
  }
  return result;
}

// =============================================================================
// Ordering engine
// =============================================================================

/**
 * Converts the flat composition counts into an ordered array of BlockSlots.
 *
 * Ordering rules (from implementationplan.md §5):
 * 1. Always start with a Vocabulary Grid (introduces the theme)
 * 2. Guided Conversations are spread evenly (never back-to-back)
 * 3. Lighter blocks (Matching, Fill-in-the-Blank) follow heavier ones (Reading, Conversation)
 * 4. "What Would You Say?" (Scenario Prompts) appears near the end
 * 5. Favorite block types (Vocab, Reading, Fill-blank, Matching) appear more frequently
 * 6. Conjugation Spotlight, if present, appears in the first half
 * 7. Translation Challenge appears in the middle third
 */
export function composeBlockOrder(input: CompositionInput): BlockSlot[] {
  const baseComp = PACE_COMPOSITION[input.pace];
  const comp =
    input.learningFocus && input.learningFocus.length > 0
      ? applyFocusAdjustments(baseComp, input.learningFocus, input.pace)
      : baseComp;
  const totalBlocks =
    comp.conversations +
    comp.vocabGrids +
    comp.fillBlanks +
    comp.readingComp +
    comp.matching +
    comp.scenarios +
    comp.translations +
    comp.conjugations +
    comp.grammarExplanations;

  // Build flat list of types to place
  const typesToPlace: BlockType[] = [];
  for (let i = 0; i < comp.vocabGrids; i++) typesToPlace.push('vocabulary_grid');
  for (let i = 0; i < comp.conversations; i++) typesToPlace.push('guided_conversation');
  for (let i = 0; i < comp.fillBlanks; i++) typesToPlace.push('fill_in_the_blank');
  for (let i = 0; i < comp.readingComp; i++) typesToPlace.push('reading_comprehension');
  for (let i = 0; i < comp.matching; i++) typesToPlace.push('matching_exercise');
  for (let i = 0; i < comp.translations; i++) typesToPlace.push('translation_challenge');
  for (let i = 0; i < comp.conjugations; i++) typesToPlace.push('conjugation_spotlight');
  for (let i = 0; i < comp.grammarExplanations; i++) typesToPlace.push('grammar_explanation');
  for (let i = 0; i < comp.scenarios; i++) typesToPlace.push('scenario_prompts');

  // Assign sub-themes (deprioritize previously used ones for subscription renewals)
  const subThemes =
    input.subThemes || generateSubThemes(input.topics, totalBlocks, input.previousMonthsSubThemes);

  // ── Ordering algorithm ──
  const ordered: BlockType[] = new Array(totalBlocks);
  const placed = new Set<number>();

  // Helper: find next empty slot at or after `start`
  const findSlot = (start: number): number => {
    for (let i = start; i < totalBlocks; i++) {
      if (!placed.has(i)) return i;
    }
    // Wrap around
    for (let i = 0; i < start; i++) {
      if (!placed.has(i)) return i;
    }
    return -1; // shouldn't happen
  };

  // Helper: place a type, return the slot index
  const place = (slot: number, type: BlockType): void => {
    ordered[slot] = type;
    placed.add(slot);
  };

  // Track remaining instances of each type
  const remaining: Record<string, number> = {};
  for (const t of typesToPlace) {
    remaining[t] = (remaining[t] || 0) + 1;
  }

  const take = (type: BlockType): boolean => {
    if ((remaining[type] || 0) > 0) {
      remaining[type]--;
      return true;
    }
    return false;
  };

  // Rule 1: First slot is always vocabulary_grid
  if (take('vocabulary_grid')) {
    place(0, 'vocabulary_grid');
  }

  // Rule 4: Scenario prompts go in the last 20% of blocks
  const scenarioStart = Math.floor(totalBlocks * 0.8);
  let scenarioSlot = scenarioStart;
  while (take('scenario_prompts')) {
    const slot = findSlot(scenarioSlot);
    if (slot >= 0) {
      place(slot, 'scenario_prompts');
      scenarioSlot = slot + 1;
    }
  }

  // Rule 6: Conjugation spotlight in the first half
  let conjSlot = 2; // after the first vocab grid
  while (take('conjugation_spotlight')) {
    const maxSlot = Math.floor(totalBlocks * 0.5);
    const slot = findSlot(Math.min(conjSlot, maxSlot));
    if (slot >= 0 && slot <= maxSlot) {
      place(slot, 'conjugation_spotlight');
      conjSlot = slot + 2;
    } else {
      // Fallback: place wherever
      const fallback = findSlot(0);
      if (fallback >= 0) place(fallback, 'conjugation_spotlight');
    }
  }

  // Grammar explanations in the first 60%, never back-to-back
  let grammarSlot = 1; // after the first vocab grid
  while (take('grammar_explanation')) {
    const maxSlot = Math.floor(totalBlocks * 0.6);
    const slot = findSlot(Math.min(grammarSlot, maxSlot));
    if (slot >= 0 && slot <= maxSlot) {
      place(slot, 'grammar_explanation');
      grammarSlot = slot + 2; // skip at least one slot to avoid back-to-back
    } else {
      // Fallback: place wherever available
      const fallback = findSlot(0);
      if (fallback >= 0) place(fallback, 'grammar_explanation');
    }
  }

  // Rule 7: Translation challenge in the middle third
  const midStart = Math.floor(totalBlocks * 0.33);
  const midEnd = Math.floor(totalBlocks * 0.66);
  let transSlot = midStart;
  while (take('translation_challenge')) {
    const slot = findSlot(transSlot);
    if (slot >= 0 && slot <= midEnd) {
      place(slot, 'translation_challenge');
      transSlot = slot + 2;
    } else {
      const fallback = findSlot(0);
      if (fallback >= 0) place(fallback, 'translation_challenge');
    }
  }

  // Rule 2: Spread conversations evenly
  const convCount = comp.conversations;
  if (convCount > 0) {
    const spacing = Math.floor(totalBlocks / (convCount + 1));
    let convSlot = spacing;
    for (let i = 0; i < convCount; i++) {
      if (take('guided_conversation')) {
        const slot = findSlot(convSlot);
        if (slot >= 0) {
          place(slot, 'guided_conversation');
          convSlot = slot + spacing;
        }
      }
    }
  }

  // Fill remaining slots with the light/favorite blocks in interleaved order
  // Rule 3 & 5: Lighter blocks after heavier ones, favorites more frequent
  const fillOrder: BlockType[] = [
    'vocabulary_grid',
    'fill_in_the_blank',
    'matching_exercise',
    'reading_comprehension',
    'vocabulary_grid',
    'matching_exercise',
  ];
  let fillIdx = 0;

  for (let slot = 0; slot < totalBlocks; slot++) {
    if (placed.has(slot)) continue;

    // Try the interleaved fill order first
    let didPlace = false;
    for (let attempts = 0; attempts < fillOrder.length; attempts++) {
      const type = fillOrder[(fillIdx + attempts) % fillOrder.length];
      if (take(type)) {
        place(slot, type);
        fillIdx = (fillIdx + attempts + 1) % fillOrder.length;
        didPlace = true;
        break;
      }
    }

    // If fill order exhausted, place whatever remains
    if (!didPlace) {
      for (const type of Object.keys(remaining) as BlockType[]) {
        if (take(type)) {
          place(slot, type);
          break;
        }
      }
    }
  }

  // Build final slots with sub-themes
  const isComplete = input.pace === 'complete';
  return ordered.map((type, i) => ({
    type,
    order: i + 1,
    subTheme: subThemes[i % subThemes.length],
    ...(isComplete ? { chapter: getChapterForBlock(i + 1, totalBlocks) } : {}),
  }));
}

/**
 * Returns the total number of exercise blocks for a given pace.
 */
export function getBlockCount(pace: PaceCode): number {
  const comp = PACE_COMPOSITION[pace];
  return (
    comp.conversations +
    comp.vocabGrids +
    comp.fillBlanks +
    comp.readingComp +
    comp.matching +
    comp.scenarios +
    comp.translations +
    comp.conjugations +
    comp.grammarExplanations
  );
}

/**
 * Returns the estimated page count for a given pace.
 */
export function getEstimatedPageCount(pace: PaceCode): number {
  const tier = PACE_TIERS.find((t) => t.code === pace);
  return tier?.pages ?? 32;
}

/**
 * Returns the composition breakdown for a pace tier (useful for admin display).
 */
export function getCompositionBreakdown(pace: PaceCode): Record<BlockType, number> {
  const comp = PACE_COMPOSITION[pace];
  return {
    guided_conversation: comp.conversations,
    vocabulary_grid: comp.vocabGrids,
    fill_in_the_blank: comp.fillBlanks,
    reading_comprehension: comp.readingComp,
    matching_exercise: comp.matching,
    scenario_prompts: comp.scenarios,
    translation_challenge: comp.translations,
    conjugation_spotlight: comp.conjugations,
    grammar_explanation: comp.grammarExplanations,
  };
}
